











































import { Component, Watch, Emit, Prop } from 'nuxt-property-decorator'
import { ModalComponentBaseVue } from '@/base'
import { cloneDeep, merge } from 'lodash'
import ModalFooter from '@/components/molecules/ModalFooter/ModalFooter.vue'
import {
  Alert,
  AlertMessage,
  CheckLendExtendRequest,
  CheckLendExtendRequestCollection,
  ErrorMessage,
  lendApi,
  LendExtendRequest
} from '@/api'
import {
  alertStore,
  dataStore,
  modalErrorStore,
  reWriteCardStore
} from '@/utils/store-accessor'
import { AlertFactory } from '@/factory'
import { PAGE_LOADING_END, PAGE_LOADING_START } from '@/config/event'
import LendExtensionTable, {
  InputItem
} from '@/components/organisms/LendExtensionTable/LendExtensionTable.vue'
import { CommandType } from '@/types/websocket'
import { AlertCodeCLT00035, AlertCodeILC00005, ReceiptPrintName } from '@/types'

@Component({
  components: {
    LendExtensionTable,
    ModalFooter
  }
})
export default class ModalLendExtend extends ModalComponentBaseVue {
  @Prop({
    type: String,
    required: true
  })
  userId!: string

  @Prop({
    type: Number,
    required: true
  })
  userRevision!: number

  @Prop({
    type: Array,
    default: () => []
  })
  collectionIds!: number[]

  @Prop({
    type: Boolean,
    default: false
  })
  isReceiptPrint!: boolean

  @Prop({
    type: Boolean,
    default: false
  })
  isSwitchReceiptPrint!: boolean

  inputItems: InputItem[] = []

  checkedCollectionIds: number[] = []

  dialog: boolean = false

  tableBodyHeight: number = 0

  isAllSelect: boolean = false

  receiptPrintSwitch: boolean = this.isSwitchReceiptPrint

  get selectItems(): InputItem[] {
    return this.inputItems.filter(item => {
      return this.checkedCollectionIds.includes(item.id)
    })
  }

  @Watch('dialog')
  initialize(newVal: boolean): void {
    if (newVal) {
      if (this.collectionIds.length !== 0) {
        this.checkedCollectionIds = cloneDeep(this.collectionIds)
      }
      this.$nextTick(this.calculateTableHeight)
    }
  }

  @Watch('dialog')
  onShowChanged(val: boolean) {
    if (val) {
      this.opened()
    } else {
      this.closed()
    }
  }

  @Watch('isAllSelect')
  toggleCheckedCollectionIds(): void {
    if (this.isAllSelect) {
      this.checkedCollectionIds = this.inputItems.map(item => item.id)
    } else {
      this.checkedCollectionIds = []
    }
  }

  mounted() {
    this.debug('mounted()')
    window.addEventListener('resize', this.onResize)
  }

  beforeDestroy() {
    this.debug('beforeDestroy()')
    window.removeEventListener('resize', this.onResize)
  }

  open(): void {
    this.dialog = true
    this.fetchLendExtend()
  }

  close(): void {
    this.dialog = false
    this.clear()
  }

  clear() {
    this.inputItems = []
    this.checkedCollectionIds = []
    this.tableBodyHeight = 0
    this.isAllSelect = false
    this.receiptPrintSwitch = false
  }

  onSelectAll(value: boolean): void {
    this.isAllSelect = value
  }

  onSelectCollectionId(collectionId: number): void {
    this.debug('onSelectCollectionId', collectionId)

    const index = this.checkedCollectionIds.indexOf(collectionId)
    if (index !== -1) {
      this.checkedCollectionIds.splice(index, 1)
    } else {
      this.checkedCollectionIds.push(collectionId)
    }
  }

  onClickPrint(): void {
    const element = document.getElementById('modal-lend-extension')
    if (element) {
      this.$webSocket.sendDialogScreenshotMessage(element)
    } else {
      this.$webSocket.sendScreenshotMessage()
    }
  }

  onResize(): void {
    this.calculateTableHeight()
  }

  async fetchLendExtend() {
    try {
      const results = await lendApi.getLendExtend({
        id: this.userId
      })

      this.inputItems = results
        .filter(item => item.lendType.code !== '50')
        .map(item => {
          return merge(item, {
            extendDueDate: {
              input: item.extendDueDate,
              errorMessage: ''
            }
          })
        })
    } catch (error) {
      if (error instanceof AlertMessage) {
        alertStore.setItems(error.alerts)
        this.$modalAlert.open({
          modalType: 'ERROR'
        })
      } else if (error instanceof ErrorMessage) {
        modalErrorStore.init(error)
        this.$modalError.open()
      } else {
        console.error(error)
      }
    }
  }

  checkLendExtend() {
    const check = async () => {
      const collections: CheckLendExtendRequestCollection[] = this.selectItems.map(
        item => {
          return {
            id: item.id,
            extendDueDate: item.extendDueDate.input,
            revision: item.revision
          }
        }
      )

      const request: CheckLendExtendRequest = {
        id: this.userId,
        collections
      }

      this.$eventBus.emit(PAGE_LOADING_START)

      try {
        const alerts = await lendApi.checkLendExtend(request)
        if (alerts.length > 0) {
          alertStore.setItems(alerts)
          this.$modalAlert.open({
            onOkClick: () => {
              setTimeout(
                () =>
                  this.lendExtend({
                    id: this.userId,
                    revision: this.userRevision,
                    collections
                  }),
                100
              )
            }
          })
        } else {
          await this.lendExtend({
            id: this.userId,
            revision: this.userRevision,
            collections
          })
        }
      } catch (error) {
        if (error instanceof AlertMessage) {
          this.setErrorMessage(error.alerts)
          alertStore.setItems(error.alerts)
          this.$modalAlert.open({
            modalType: 'ERROR'
          })
        } else if (error instanceof ErrorMessage) {
          modalErrorStore.init(error)
          this.$modalError.open()
        } else {
          console.error(error)
        }
      } finally {
        this.$eventBus.emit(PAGE_LOADING_END)
      }
    }

    if (!this.$webSocket.reWriteCardEnabled) {
      check()
      return
    }

    if (
      reWriteCardStore.registered &&
      reWriteCardStore.loadedUserNo !== this.userId
    ) {
      alertStore.setItem(AlertFactory.create(AlertCodeCLT00035))
      this.$modalAlert.open({
        onOkClick: check
      })
      return
    }

    check()
  }

  async lendExtend(request: LendExtendRequest) {
    this.$eventBus.emit(PAGE_LOADING_START)

    try {
      const receiptData = await lendApi.lendExtend(request)

      // 真偽値が二つある為ややこしいが、「isReceiptPrint」はダイアログ外部でレシート印刷をするか制御をする為にある
      // 「receiptPrintSwitch」はダイアログ内部の切替ボタンでレシート印刷をするか制御をする為にある
      if (
        !reWriteCardStore.loaded &&
        (this.isReceiptPrint || this.receiptPrintSwitch)
      ) {
        this.$webSocket.sendReceiptPrintMessage({
          command: CommandType.ReceiptPrintLend,
          name: ReceiptPrintName.ReceiptPrintLend,
          data: receiptData,
          subject: `利用者番号：${this.userId}`,
          customerCode: dataStore.customerCode
        })
      }

      this.$emit('updated')

      this.close()
    } catch (error) {
      if (error instanceof AlertMessage) {
        this.setErrorMessage(error.alerts)
        alertStore.setItems(error.alerts)
        this.$modalAlert.open({
          modalType: 'ERROR'
        })
      } else if (error instanceof ErrorMessage) {
        modalErrorStore.init(error)
        this.$modalError.open()
      } else {
        console.error(error)
      }
    } finally {
      this.$eventBus.emit(PAGE_LOADING_END)
    }
  }

  setErrorMessage(alerts: Alert[]) {
    alerts.forEach(alert => {
      if (alert.attribute === '') {
        return
      }
      const itemIndex = this.inputItems.findIndex(
        item => item.id === this.selectItems[alert.index].id
      )
      if (alert.subAttribute === 'extend_due_date') {
        this.inputItems[itemIndex].extendDueDate.errorMessage = alert.message
      }
    })
  }

  calculateTableHeight(): void {
    this.tableBodyHeight = 0

    this.$nextTick(() => {
      this.tableBodyHeight = this.calculateTableHeightBase()
    })
  }

  @Emit('opened')
  opened(): void {}

  @Emit('closed')
  closed(): void {}

  @Emit('ok')
  onClickOk() {
    if (this.selectItems.length === 0) {
      const alert: Alert = AlertFactory.create(
        AlertCodeILC00005,
        '貸出延長',
        '資料'
      )
      alertStore.setItem(alert)
      this.$modalAlert.open()
      return
    }

    this.checkLendExtend()
  }

  @Emit('cancel')
  onClickCancel(): void {
    this.close()
  }
}
